import clsx from 'clsx';
import {addDays} from 'date-fns';
import {useFormik} from 'formik';
import {get, isNil, isNull} from 'lodash';
import {useEffect, useState} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import invariant from 'tiny-invariant';
import * as Yup from 'yup';
import {extractReadableApiError} from '../../../../common/api/common.api';
import {formatPerfectDate} from '../../../../common/utilities/date.utility';
import {RootState} from '../../../../setup';
import {countries} from '../../../data/country_list';
import {industries} from '../../../data/industries_list';
import {UserModel, UserRoleEnum} from '../../../models/UserModel';
import {
	getCompany,
	replicateCompany,
	testSms,
	updateCompany,
	updateCompanySmsSettings,
} from '../../../modules/CRUD/CRUD';
import {UpdateCompanySmsSettings} from '../../../types/CompanyType';

function CompanyProfileEdit() {
	const user: UserModel = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel;
	const isSeniorEngineer = user?.roles?.includes(UserRoleEnum.SecchiEngineerSenior);
	const { companyId } = useParams();
	invariant(companyId, 'Company id falsy in this route');
	const token: string = useSelector<RootState>(({ auth }) => auth.accessToken, shallowEqual) as string;
	const [loading, setLoading] = useState(false);
	const [initialValues, setInitialValues] = useState({
		name: '',
		size: '',
		website: '',
		isSmsEnabled: true,
		industry: '',
		country: '',
		rollover: 365,
	});
	const [smsInitialValues, setSmsInitialValues] = useState({
		phoneNumber: '',
		createdAt: '',
		expiresAt: '',
		token: '',
		accountId: '',
	});
	const [smsNumber, setSmsNumber] = useState<string | null>(null);
	const [ytelAccountId, setYtelAccountId] = useState<string | null>(null);
	const [newInstanceName, setNewInstanceName] = useState<string>();
	const navigate = useNavigate();

	const smsFormSchema = Yup.object().shape({
		phoneNumber: Yup.string().matches(/^\+1\d{10}$/, `Number should be in format +11234567890. Format: + symbol, then country code and number (no symbols/spaces)`).optional(),
		createdAt: Yup.string().matches( /\d{4}-\d{2}-\d{2}/).when('token', {
			is: (token: string) => token === '',
			then: schema => schema.required(),
			otherwise: schema => schema.optional(),
		}),
		expiresAt: Yup.string().matches( /\d{4}-\d{2}-\d{2}/).when('token', {
			is: (token: string) => token === '',
			then: schema => schema.required(),
			otherwise: schema => schema.optional(),
		}),
		token: Yup.string().optional(),
		accountId: Yup.string().matches(/^(?:\{{0,1}(?:[0-9a-fA-F]){8}-(?:[0-9a-fA-F]){4}-(?:[0-9a-fA-F]){4}-(?:[0-9a-fA-F]){4}-(?:[0-9a-fA-F]){12}\}{0,1})$/).optional(),
	})

	const registrationSchema = Yup.object().shape({
		name: Yup.string()
						 .required('Company name is required'),
		size: Yup.string()
						 .required('Company size is required'),
		industry: Yup.string()
								 .required('Industry is required'),
		country: Yup.string()
								.required('Country is required'),
		rollover: Yup.string()
								 .required('Rollover is required'),
	});

	const handleTestSms = async () => {
		await testSms(companyId, token)
		.then(() => {
			toast.success('Successfully tested SMS');
		})
		.catch(err => {
			console.error(err);
			toast.error('Error testing SMS');
		});
	}

	const formik = useFormik({
		enableReinitialize: true,
		initialValues,
		validationSchema: registrationSchema,
		onSubmit: (values,
							 {
								 setStatus,
								 setSubmitting,
							 }) => {
			setLoading(true);
			setTimeout(() => {
				updateCompany(
					companyId,
					{
						name: values.name,
						size: values.size,
						website: values.website,
						industry: values.industry,
						country: values.country,
						rollover: values.rollover,
						isSmsEnabled: values.isSmsEnabled,
					},
					token,
				)
					.then(({ data }) => {
						setLoading(false);
						// history.push('/companies')
					})
					.catch((err) => {
						console.log(err);
            toast.error(err.response.data.message);
						setLoading(false);
					});
			}, 1000);
		},
	});

	const smsFormik = useFormik({
		enableReinitialize: true,
		validateOnMount: true,
		validateOnChange: true,
		initialValues: smsInitialValues,
		validationSchema: smsFormSchema,
		onSubmit: (values) => {
			setLoading(true);
			let payload: Partial<UpdateCompanySmsSettings> = {};
			if (isNull(smsNumber) && !isNull(values.phoneNumber)) {
				payload = {
					phone_number: values.phoneNumber,
				};
			}
			if (isSeniorEngineer) {
				if (values.token) {
					payload = {
						...payload,
						token: values.token,
						created_at: values.createdAt,
						expires_at: values.expiresAt,
					};
				}
				if (values.accountId && !ytelAccountId) {
					payload = {
						...payload,
						account_id: values.accountId,
					};
				}
			}
			if (!Object.keys(payload).length) {
				return;
			}
			updateCompanySmsSettings(
				companyId,
				payload,
				token,
			)
				.then(({ data }) => {
					if (data.success) {
						toast.success(data.msg || 'Successfully tested SMS');
					} else if (data.error) {
						toast.error(data.msg || 'Error testing SMS');
					}
					setLoading(false);
				})
				.catch((err) => {
					toast.error(err.response.data.message || 'Error testing SMS');
					setLoading(false);
				});
		},
	});

	const handleReplicateCompany = async () => {
		if (!newInstanceName) return;
		await replicateCompany(companyId, newInstanceName, token)
			.then(({data}) => {
				toast.success(`Successfully replicated company`);
				const newTenantId = get(data, 'results.company.id');
				if (newTenantId) {
					navigate(`/edit-company/${newTenantId}`);
				}
			})
		.catch(err => {
			toast.error(extractReadableApiError(err, 'Error replicating company'));
		})
	}

	useEffect(() => {
		getCompany(companyId, token)
			.then(({ data }) => {
				const number = get(data, 'settings.sms.phone_number', null);
				const acctId = get(data, 'settings.sms.account_id', null);
				setSmsNumber(number);
				setYtelAccountId(acctId);
				setSmsInitialValues({
							phoneNumber: number || '',
							token: '',
							createdAt: get(data, 'settings.sms.created_at', ''),
							expiresAt: get(data, 'settings.sms.expires_at', ''),
							accountId: get(data, 'settings.sms.account_id', ''),
				});
				setInitialValues({
					name: data.name,
					size: data.size,
					website: data.website,
					isSmsEnabled: data.isSmsEnabled,
					industry: data.industry,
					country: data.country,
					rollover: data.rollover,
				});
			});
	}, [companyId, token]);

	return (
		<>
			<div className="card edit_company_card">
				<div className="card-header border-0 pt-5"></div>
				<div className="card-body py-3">
					<div className="p-lg-15 p-10 bg-white rounded mx-auto">
						<form
							className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework"
							noValidate
							id="kt_login_signup_form"
							onSubmit={formik.handleSubmit}

						>
							<div className="row fv-row mb-7">
								<label className="col-lg-4 col-form-label fw-bolder text-dark fs-6">Company Name</label>
								<div className="col-lg-8">
									<input
										placeholder="Company Name"
										type="text"
										autoComplete="off"
										{...formik.getFieldProps('name')}
										className={clsx(
											'form-control form-control-lg form-control-solid',
											{
												'is-invalid': formik.touched.name && formik.errors.name,
											},
											{
												'is-valid': formik.touched.name && !formik.errors.name,
											},
										)}
									/>
									{formik.touched.name && formik.errors.name && (
										<div className="fv-plugins-message-container">
											<div className="fv-help-block">
												<span role="alert">{formik.errors.name}</span>
											</div>
										</div>
									)}
								</div>
							</div>

							<div className="row fv-row mb-7">
								<label className="col-lg-4 col-form-label fw-bolder text-dark fs-6">Company Size</label>
								<div className="col-lg-8">
									<select
										{...formik.getFieldProps('size')}
										className={clsx(
											'form-control form-control-lg form-control-solid',
											// {
											//     'is-invalid': formik.touched.role && formik.errors.role,
											// },
											// {
											//     'is-valid': formik.touched.role && !formik.errors.role,
											// }
										)}
										style={{ appearance: 'auto' }}
									>
										<option value=""></option>
										<option value="Small">0-50</option>
										<option value="Medium">51-250</option>
										<option value="Large">251-499</option>
										<option value="Enterprise">500+</option>
									</select>
									{formik.touched.size && formik.errors.size && (
										<div className="fv-plugins-message-container">
											<div className="fv-help-block">
												<span role="alert">{formik.errors.size}</span>
											</div>
										</div>
									)}
								</div>
							</div>

							<div className="row fv-row mb-7">
								<label className="col-lg-4 col-form-label fw-bolder text-dark fs-6">Website</label>
								<div className="col-lg-8">
									<input
										placeholder="Website"
										type="text"
										autoComplete="off"
										{...formik.getFieldProps('website')}
										className={clsx(
											'form-control form-control-lg form-control-solid',
											{
												'is-invalid': formik.touched.website && formik.errors.website,
											},
											{
												'is-valid': formik.touched.website && !formik.errors.website,
											},
										)}
									/>
									{formik.touched.website && formik.errors.website && (
										<div className="fv-plugins-message-container">
											<div className="fv-help-block">
												<span role="alert">{formik.errors.website}</span>
											</div>
										</div>
									)}
								</div>
							</div>

							<div className="row fv-row mb-7">
								<label className="col-lg-4 col-form-label fw-bolder text-dark fs-6">Industry</label>
								<div className="col-lg-8">
									<select
										{...formik.getFieldProps('industry')}
										className={clsx(
											'form-control form-control-lg form-control-solid',
										)}
										style={{ appearance: 'auto' }}
									>
										<option value=""></option>
										{
											industries.map(industry => (
												<option
													key={industry}
													value={industry}>{industry}</option>
											))
										}
									</select>
									{formik.touched.industry && formik.errors.industry && (
										<div className="fv-plugins-message-container">
											<div className="fv-help-block">
												<span role="alert">{formik.errors.industry}</span>
											</div>
										</div>
									)}
								</div>
							</div>

							<div className="row fv-row mb-7">
								<label className="col-lg-4 col-form-label fw-bolder text-dark fs-6">Country</label>
								<div className="col-lg-8">
									<select
										{...formik.getFieldProps('country')}
										className={clsx(
											'form-control form-control-lg form-control-solid',
										)}
										style={{ appearance: 'auto' }}
									>
										<option value=""></option>
										{
											countries.map(country => (
												<option
													key={country}
													value={country}>{country}</option>
											))
										}
									</select>
									{formik.touched.country && formik.errors.country && (
										<div className="fv-plugins-message-container">
											<div className="fv-help-block">
												<span role="alert">{formik.errors.country}</span>
											</div>
										</div>
									)}
								</div>
							</div>

							<div className="row fv-row mb-10">
								<label className="col-lg-4 col-form-label fw-bolder text-dark fs-6">Rollover</label>
								<div className="col-lg-8">
									<select
										{...formik.getFieldProps('rollover')}
										className={clsx(
											'form-control form-control-lg form-control-solid',
										)}
										style={{ appearance: 'auto' }}
									>
										<option value=""></option>
										<option value="90">3 Months</option>
										<option value="180">6 Months</option>
										<option value="270">9 Months</option>
										<option value="365">12 Months</option>
										<option value="465">18 Months</option>
										<option value="730">24 Months</option>
									</select>
									{formik.touched.rollover && formik.errors.rollover && (
										<div className="fv-plugins-message-container">
											<div className="fv-help-block">
												<span role="alert">{formik.errors.rollover}</span>
											</div>
										</div>
									)}
								</div>
							</div>

							<div>
								<div className="form-check form-check-custom form-check-solid">
									<input
										className="form-check-input"
										type="checkbox"
										id="flexCheckDefault"
										{...formik.getFieldProps('isSmsEnabled')}
										checked={formik.values.isSmsEnabled}
									/>
									<label
										className="form-check-label fw-bolder text-dark fs-6"
										htmlFor="flexCheckDefault">
										SMS Enabled
									</label>
								</div>
							</div>

							<div className="text-center mt-10">
								<button
									type="submit"
									id="kt_sign_up_submit"
									className="btn btn-lg btn-primary min-w-150px mb-5"
									disabled={formik.isSubmitting || !formik.isValid}
								>
									{!loading && <span className="indicator-label">Update</span>}
									{loading && (
										<span
											className="indicator-progress"
											style={{ display: 'block' }}>
                                            Please wait...{' '}
											<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                        </span>
									)}
								</button>
							</div>
						</form>
							<div className="row mb-7 border-top border-dark-subtle pt-2">
								<span className="fs-2">SMS Settings</span>
								<div>
									<button
										type="button"
										className="btn btn-outline-info d-inline-block"
										onClick={handleTestSms}
										disabled={!user.phone || !smsNumber}
									>
										Test SMS
									</button>
									{!user.phone && (
										<>
											<span className="text-danger">Must have your phone number set to test.</span>
										</>
									)}
								</div>
								<div>
									<form className="">
										<div className="row fv-row mb-7">
											<label className="col-lg-4 col-form-label fw-bolder text-dark fs-6">Phone Number</label>
											<div className="col-lg-8">
												<input
													placeholder="None"
													type="text"
													autoComplete="off"
													disabled={!isNil(smsNumber)}
													onFocus={() => smsFormik.setFieldTouched('phoneNumber')}
													{...smsFormik.getFieldProps('phoneNumber')}
													className={clsx(
														'form-control form-control-lg form-control-solid',
														{
															'border border-danger': smsFormik.touched.phoneNumber && smsFormik.errors.phoneNumber,
														},
													)}
												/>
												{!smsNumber && (
													<>
														<span className="text-dark fst-italic d-block mt-1">Please ensure +1 prefixes US phone numbers.</span>
													</>
												)}
												{smsNumber && (
													<>
													<span className="fst-italic d-block mt-1">
														<i className="bi bi-exclamation-triangle text-danger me-2" />
														Cannot change phone number that's already set. Contact Engineering to change a phone number.
													</span>
													</>
												)}
												{smsFormik.touched.phoneNumber && smsFormik.errors.phoneNumber && (
													<div className="">
														<div className="text-danger">
															<span role="alert">{smsFormik.errors.phoneNumber}</span>
														</div>
													</div>
												)}
											</div>

											<div className="row fv-row mb-7">
												<label className="col-lg-4 col-form-label fw-bolder text-dark fs-6">Ytel Subaccount ID</label>
												<div className="col-lg-8">
													<input
														placeholder="None"
														type="text"
														autoComplete="off"
														disabled={!isSeniorEngineer || !!ytelAccountId}
														onFocus={() => smsFormik.setFieldTouched('accountId')}
														{...smsFormik.getFieldProps('accountId')}
														className={clsx(
															'form-control form-control-lg form-control-solid',
														)}
													/>
													{!ytelAccountId && (
														<span className="text-dark fst-italic d-block mt-1">
														Use the main account if they will be on the Secchi mainline. Otherwise if they will have a custom number, use the account_id from that subaccount.
													</span>
													)}
													{ytelAccountId && (
														<>
														<span className="fst-italic d-block mt-1">
															<i className="bi bi-exclamation-triangle text-danger me-2" />
																Cannot change Ytel account_id that's already set. Contact Engineering to change Ytel subaccounts.
														</span>
														</>
													)}
												</div>
											</div>
										</div>
										<div className="border border-1 border-danger rounded p-2">

											<fieldset>
												<legend className="fs-3">
													Engineering Zone
												</legend>
											</fieldset>

											<div className="row fv-row mb-7">
												<label className="col-lg-4 col-form-label fw-bolder text-dark fs-6">New Token</label>
												<div className="col-lg-8">
													<input
														placeholder="None"
														type="text"
														autoComplete="off"
														disabled={!isSeniorEngineer}
														onFocus={() => smsFormik.setFieldTouched('token')}
														{...smsFormik.getFieldProps('token')}
														onPaste={(event) => {
															smsFormik.setFieldValue('createdAt', formatPerfectDate(new Date()));
															smsFormik.setFieldValue('expiresAt', formatPerfectDate(addDays(new Date(), 185)));
														}}
														className={clsx(
															'form-control form-control-lg form-control-solid',
														)}
													/>
													<span className="text-dark fst-italic">Please ensure to select only reporting and contacts for roles.</span>
												</div>
											</div>

											<div className="row fv-row mb-7">
												<label className="col-lg-4 col-form-label fw-bolder text-dark fs-6">Created At</label>
												<div className="col-lg-8">
													<input
														placeholder="None"
														type="text"
														autoComplete="off"
														disabled={!isSeniorEngineer || !smsFormik.values.createdAt}
														{...smsFormik.getFieldProps('createdAt')}
														className={clsx(
															'form-control form-control-lg form-control-solid',
														)}
													/>
													<span className="text-dark fst-italic">Can only update when setting token. Format: YYYY-MM-DD</span>
												</div>
											</div>

											<div className="row fv-row mb-7">
												<label className="col-lg-4 col-form-label fw-bolder text-dark fs-6">Expires At</label>
												<div className="col-lg-8">
													<input
														placeholder="None"
														type="text"
														autoComplete="off"
														disabled={!isSeniorEngineer || !smsFormik.values.expiresAt}
														{...smsFormik.getFieldProps('expiresAt')}
														className={clsx(
															'form-control form-control-lg form-control-solid',
														)}
													/>
													<span className="text-dark fst-italic">Can only update when setting token. SOP is 185 days expiry. Format: YYYY-MM-DD.</span>
												</div>
											</div>
										</div>

										<div className="text-center mt-10">
											<button
												type="button"
												className="btn btn-lg btn-primary min-w-150px mb-5"
												disabled={(smsFormik.isSubmitting || !(smsFormik.isValid && smsFormik.touched && smsFormik.dirty) || (!smsFormik.touched.phoneNumber && !smsFormik.touched.accountId && !smsFormik.touched.token))}
												onClick={() => smsFormik.submitForm()}
											>
												{!loading && <span className="indicator-label">Update SMS Settings</span>}
												{loading && (
													<span
														className="indicator-progress"
														style={{display: 'block'}}>
                                            Please wait...{' '}
														<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                        </span>
												)}
											</button>
										</div>
									</form>
								</div>
							</div>


						<div className="row mb-7 border-top border-dark-subtle pt-2">
							<span className="fs-2">Copy to new instance</span>
							<label className="col-lg-4 col-form-label fw-bolder text-dark fs-6">New Instance Name</label>
							<div className="col-lg-8">
								<input
									type="text"
									autoComplete="off"
									className={clsx(
										'form-control form-control-lg form-control-solid',
									)}
									onChange={evt => setNewInstanceName(evt.target.value)}
								/>
							</div>
							<div>
								<button
									type="button"
									className="btn btn-lg btn-primary min-w-150px mb-5 d-block"
									disabled={!newInstanceName}
									onClick={() => handleReplicateCompany()}
								>
									{!loading && <span className="indicator-label">Replicate</span>}
									{loading && (
										<span
											className="indicator-progress"
											style={{display: 'block'}}>
                                            Please wait...{' '}
											<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                        </span>
									)}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}

export { CompanyProfileEdit };
